import { Truck, Weight, Container, Warehouse } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const SERVICES = [
  {
    id: "ftl",
    icon: Truck,
    title: "Full Truck Load",
    tag: "FTL",
    body: "Dedicated full-truck movements for industrial freight — from 9-tonne rigs to 32-ft containerised vehicles across all major lanes.",
    specs: "9T / 16T / 25T · 20FT & 32FT containers · Door-to-door",
    img: "https://images.pexels.com/photos/29057947/pexels-photo-29057947.jpeg?auto=compress&cs=tinysrgb&w=900",
    alt: "Loaded Tata full truck on an Indian highway",
  },
  {
    id: "odc",
    icon: Weight,
    title: "ODC & Project Cargo",
    tag: "Heavy Haulage",
    body: "Over-dimensional consignments engineered move by move — route surveys, permissions and escort coordination handled end to end.",
    specs: "Route survey · Hydraulic axles · Escorts & permits",
    img: "https://images.pexels.com/photos/32831510/pexels-photo-32831510.jpeg?auto=compress&cs=tinysrgb&w=900",
    alt: "Wind turbine blades and cranes at a project cargo site",
  },
  {
    id: "trailer",
    icon: Container,
    title: "Trailer Transportation",
    tag: "Flatbed · Multi-Axle · Low-Bed",
    body: "Flatbed, multi-axle and low-bed trailers for heavy, long and awkward loads that standard trucks simply can't take.",
    specs: "Flatbed · Low-bed · Semi low-bed · Multi-axle",
    img: "https://images.pexels.com/photos/38095094/pexels-photo-38095094.jpeg?auto=compress&cs=tinysrgb&w=900",
    alt: "Backhoe loader chained to a low-bed flatbed trailer",
  },
  {
    id: "warehousing",
    icon: Warehouse,
    title: "Warehousing & Storage",
    tag: "Storage & Distribution",
    body: "Secure racked storage and staging near industrial corridors, with inventory visibility and distribution support built in.",
    specs: "Racked & bulk storage · Inventory management · Distribution",
    img: "https://images.pexels.com/photos/10697106/pexels-photo-10697106.jpeg?auto=compress&cs=tinysrgb&w=900",
    alt: "Organised warehouse with tall racks and stacked pallets",
  },
];

export const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="scroll-mt-20 py-24 sm:py-32 bg-[#0D1117]"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHead
        overline="What We Move"
        title="Four lines. One standard."
        sub="Every service line runs on the same operating discipline — checked equipment, tracked consignments, documented billing."
      />

      <div className="mt-14 grid md:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={(i % 2) * 0.1}>
            <article
              data-testid={`service-card-${s.id}`}
              className="group bg-[#161B22] hover:bg-[#1A212B] transition-colors duration-300 h-full"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[60%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#161B22] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#0D1117]/90 border border-slate-700 px-2.5 py-1.5">
                  <s.icon className="w-4 h-4 text-amber-500" />
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                    {s.tag}
                  </span>
                </div>
              </div>
              <div className="p-7 border-t-2 border-transparent group-hover:border-amber-500 transition-colors duration-300">
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wide text-slate-100">
                  {s.title}
                </h3>
                <p className="mt-3 text-base text-slate-400 leading-relaxed">{s.body}</p>
                <p className="mono mt-5 pt-4 border-t border-slate-800 text-[11px] uppercase tracking-[0.15em] text-amber-500/80">
                  {s.specs}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
